import { COMPASS_KINDS, COMPASS_MAX_PIPS, chooseCompassPips, stackCompassPips, type CompassInputs, type CompassKind } from "./compass.ts";

/**
 * One slot of the pip buffer: what a pip is, the handle and name it stands for (a quest's title; empty for the pin),
 * the roster place that gives a party member's colour, its offset from the player in tiles, and its place on the band's
 * middle line in the slot's logical pixels. The fields are written in place.
 */
export interface CompassSlot {
  kind: CompassKind;
  handle: number;
  name: string;
  place: number;
  dx: number;
  dy: number;
  x: number;
  y: number;
}

/**
 * The pips the band draws, in a buffer made once: `count` slots of `slots` are in use, in draw order with the top
 * pip first.
 */
export interface CompassPipBuffer {
  count: number;
  readonly slots: CompassSlot[];
}

/**
 * Makes the pip buffer, with `COMPASS_MAX_PIPS` slots, holding none.
 *
 * @returns The buffer.
 */
export function createCompassPipBuffer(): CompassPipBuffer {
  const slots: CompassSlot[] = [];
  for (let at = 0; at < COMPASS_MAX_PIPS; at += 1) {
    slots.push({ kind: COMPASS_KINDS[0], handle: 0, name: "", place: 0, dx: 0, dy: 0, x: 0, y: 0 });
  }
  return { count: 0, slots };
}

/**
 * Refills the buffer from what the page holds: the pips `chooseCompassPips` gives, spread by `stackCompassPips` so that
 * every one shows whole, written into the slots already there. It reports whether anything the band draws changed (the
 * count, or a slot's kind, colour place or pixel), so a frame in which no pip moved a pixel repaints nothing.
 *
 * @param buffer The buffer to refill.
 * @param input What the page holds.
 * @returns True when the buffer now draws differently than it did.
 * @throws RangeError as `chooseCompassPips` and `stackCompassPips` do.
 */
export function refreshCompassPips(buffer: CompassPipBuffer, input: CompassInputs): boolean {
  const chosen = chooseCompassPips(input);
  const pips = chosen.length > 1 ? stackCompassPips(chosen) : chosen;
  let changed = buffer.count !== pips.length;
  for (let at = 0; at < pips.length; at += 1) {
    const pip = pips[at];
    const slot = buffer.slots[at];
    if (pip === undefined || slot === undefined) {
      continue;
    }
    if (slot.kind !== pip.kind || slot.place !== pip.place || slot.x !== pip.x || slot.y !== pip.y) {
      changed = true;
    }
    slot.kind = pip.kind;
    slot.handle = pip.handle;
    slot.name = pip.name;
    slot.place = pip.place;
    slot.dx = pip.dx;
    slot.dy = pip.dy;
    slot.x = pip.x;
    slot.y = pip.y;
  }
  buffer.count = pips.length;
  return changed;
}
